import { Info, X } from 'lucide-react';
import { orDash } from '../../../../utils/schema-parse';
import formatDecimal from '../../../../utils/format-decimal';
import { formatPeriodo } from '../../../../utils/format-date';
import type { RelatorioVisita } from '../../schemas/payload.schema';
import formatArea from '../../../../utils/format-medida';

interface RecomendacoesDetalhesModalProps {
  recomendacoes: NonNullable<RelatorioVisita['recomendacoes']>;
  open: boolean;
  onClose: () => void;
}

const InfoCell = ({ label, value }: { label: string; value: string }) => (
  <div className="rv-recdet-info">
    <span className="rv-recdet-label">{label}</span>
    <span className="rv-recdet-info-value">{value}</span>
  </div>
);

const RecomendacoesDetalhesModal = ({
  recomendacoes,
  open,
  onClose,
}: RecomendacoesDetalhesModalProps) => {
  if (!open) return null;

  return (
    <div
      role="presentation"
      className="rv-recdet-overlay"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape') onClose();
      }}
    >
      <div role="dialog" aria-modal="true" className="rv-recdet-dialog">
        <div className="rv-recdet-header">
          <h2 className="rv-recdet-title">Recomendações Técnicas Detalhadas</h2>
          <button
            type="button"
            aria-label="Fechar"
            className="rv-recdet-close"
            onClick={onClose}
          >
            <X size={20} />
          </button>
        </div>

        <div className="rv-recdet-body">
          <div className="rv-recdet-banner">
            <Info className="rv-recdet-banner-icon" size={18} />
            As recomendações abaixo devem ser seguidas rigorosamente para
            garantir a eficácia do manejo.
          </div>

          {recomendacoes.map((recomendacao, index) => {
            const periodo = formatPeriodo(
              recomendacao.dataInicio,
              recomendacao.dataFim,
            );
            const area = formatArea(recomendacao.gleba?.areaHa);
            const produtos = recomendacao.produtos ?? [];

            return (
              <div
                // eslint-disable-next-line react/no-array-index-key
                key={`${recomendacao.id ?? recomendacao.gleba?.id}-${index}`}
                className="rv-recdet-card"
              >
                <div className="rv-recdet-card-head">
                  <div>
                    <div className="rv-recdet-card-title-row">
                      <span className="rv-recdet-gleba">
                        {orDash(recomendacao.gleba?.descricao)}
                      </span>
                      {recomendacao.id != null ? (
                        <span className="rv-recdet-codigo">
                          Cód: {recomendacao.id}
                        </span>
                      ) : null}
                    </div>
                    <div className="rv-recdet-atividade">
                      {orDash(recomendacao.atividade)}
                      {area ? ` · ${area}` : ''}
                    </div>
                  </div>

                  <div className="rv-recdet-when">
                    {periodo ? (
                      <span className="rv-recdet-aplicar">
                        Aplicar: {periodo}
                      </span>
                    ) : null}
                    {recomendacao.periodo && recomendacao.periodo !== '-' ? (
                      <div className="rv-recdet-epoca">
                        Período: {recomendacao.periodo}
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="rv-recdet-grid">
                  <InfoCell
                    label="Recomendante"
                    value={orDash(recomendacao.recomendante)}
                  />
                  <InfoCell
                    label="Vazão"
                    value={
                      recomendacao.vazao == null
                        ? '-'
                        : `${recomendacao.vazao} l/ha`
                    }
                  />
                  <InfoCell label="Bico" value={orDash(recomendacao.bico)} />
                </div>

                <div className="rv-recdet-produtos">
                  <div className="rv-recdet-produtos-head">
                    <span className="rv-recdet-label">Produtos</span>
                    <span className="rv-recdet-label">Dose</span>
                  </div>
                  <div className="rv-recdet-produtos-list">
                    {produtos.length > 0 ? (
                      produtos.map((produto, produtoIndex) => (
                        <div
                          // eslint-disable-next-line react/no-array-index-key
                          key={`${produto.descricao}-${produtoIndex}`}
                          className="rv-recdet-produto"
                        >
                          <span className="rv-recdet-produto-name">
                            {orDash(produto.descricao)}
                          </span>
                          <span className="rv-recdet-dose">
                            {formatDecimal(produto.dose)}{' '}
                            {orDash(produto.unidade)}
                          </span>
                        </div>
                      ))
                    ) : (
                      <span className="rv-recdet-empty">-</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default RecomendacoesDetalhesModal;
